/**
 * @fileoverview Fetch the caller's regime profile — how this regime's numbers
 * read — when a wizard opens.
 *
 * The regime is derived from `declarant.regulation` on the server and is never
 * sent by the client (D3/RD-2): a client that could name its regime could pick
 * its rules. So the browser asks, rather than deciding, and what comes back is
 * presentation only — currency — because every verdict is re-derived
 * server-side on every write (V5).
 *
 * Three properties this hook is shaped by:
 *
 *   - **It fetches when the wizard opens, not at module load** (V1). The UK
 *     flow config is constructed on `/imports` too — an EU page that never
 *     renders it — so a fetch tied to construction would put a request on a
 *     page that has nothing to do with it. `enabled` is the caller's switch.
 *   - **It answers with `null` until it has an answer.** The host holds the
 *     wizard until then (`isReady`), because the engine captures the flow's
 *     rows config at mount and a currency arriving afterwards would never
 *     reach the totals. One fetch, one loading state — the cost D3 names.
 *   - **A failure is not a blocked wizard.** If the endpoint is unreachable
 *     the wizard opens with no currency and totals render as bare numbers: the
 *     import still works, and nobody is shown the wrong symbol (F-CUR).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import http from 'helpers/requests';
import { setRegimeCurrency } from 'stores/useRegimeCurrencyStore';
import queryClient from '../../../../queryClient';

export const REGIME_PROFILE_ENDPOINT = '/imports/regime-profile';

/** How long the wizard waits for its currency before opening without one. */
const PROFILE_TIMEOUT_MS = 5000;

/**
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ profile: Object|null, isReady: boolean }} `isReady` is true once
 *   the answer is in — or once asking has failed, which is still an answer.
 */
export function useRegimeProfile({ enabled = true } = {}) {
  const { data, isSuccess, isError } = useQuery(
    {
      queryKey: ['imports-regime-profile'],
      queryFn: async () => {
        // A wizard must not be held open by a formatter. There is no global
        // axios timeout, so a hung request would be a wizard that never opens
        // — worse than a total with no symbol on it, which is what a timeout
        // degrades to.
        const response = await http.get(REGIME_PROFILE_ENDPOINT, { timeout: PROFILE_TIMEOUT_MS });
        return response?.data ?? null;
      },
      enabled,
      // A declarant's regulation does not change while they are looking at a
      // table, and a wizard reopened twice in a minute should not re-ask.
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: 1,
    },
    queryClient,
  );

  // `currency` is the only member read anywhere; a body without one (an
  // endpoint that does not exist yet, a stub answering `{}`) is the same
  // situation as a failure and is treated as one.
  const profile = data && typeof data.currency === 'string' ? data : null;

  // Publish it to the app-wide store so the formatters that cannot call a hook
  // — the Excel and PDF generators, the chart tick callbacks, every plain
  // `formatCurrency(value)` — read the same answer this hook got, from the same
  // request. One fetch, one source (D3): the store is written here and nowhere
  // else, so it cannot disagree with what a component using this hook renders.
  // Never written back to `null` on a failed fetch: `getRegimeCurrency()`
  // already answers 'EUR' for an empty store, and blanking it here would let a
  // background refetch error repaint a resolved UK page in euros.
  useEffect(() => {
    if (profile?.currency) setRegimeCurrency(profile.currency);
  }, [profile?.currency]);

  // `isFailed`: the request errored and left no answer. Not the same as an
  // answer without a currency (a stub's `{}`), which stays EU as before: a
  // failure says nothing about the regime, so a caller must not default as if
  // it were EU (a € EEX price would then be saved as £ on a UK quote).
  return { profile, isReady: isSuccess || isError, isFailed: isError && !profile };
}

export default useRegimeProfile;
